import React, { FC, useMemo } from 'react'
import { Button, Group, Select } from '@mantine/core'
import useTranslation from 'next-translate/useTranslation'

import { useBuilderFormContext, useBuilderSharedDataContext } from 'components/business/CampaignBuilder'
import StyleItem from 'components/business/CampaignBuilder/StyleItem'
import { Direction, Locale, useCheckToggleQuery, WordingsInput } from 'types/domain'
import useWordingsModal from './useWordingsModal'
import getDefaultLocaleWords from './utils/getDefaultLocaleWords'
import { FeatureToggle } from 'config/constants'
import useCurrentUserCache from 'components/business/hooks/useCurrentUserCache'

type Props = {}
const WidgetLocale: FC<Props> = () => {
  const { t } = useTranslation('componentCampaignBuilder')
  const form = useBuilderFormContext()
  const { application, campaignType } = useBuilderSharedDataContext()
  const { account } = useCurrentUserCache()

  const { data: toggleData } = useCheckToggleQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      input: {
        identifier: account?.id,
        name: FeatureToggle.MultilangDirection,
      },
    },
    skip: !account?.id,
  })
  const multilangEnabled = toggleData?.checkToggle ?? false

  const availableLocales = multilangEnabled ? [Locale.ZhCn, Locale.En, Locale.Other] : [Locale.ZhCn, Locale.En]

  const handleDirectionChange = (direction: Direction) => {
    form.setFieldValue('formSetting.textStyle.direction', direction)
  }

  const handleConfirm = (wordings: WordingsInput, direction: Direction) => {
    const { locale } = form.values.localeSetting!
    form.setFieldValue('localeSetting', { locale, wordings })
    form.setFieldValue('formSetting.textStyle.direction', direction)
  }

  const openModal = useWordingsModal({
    localeSetting: form.values.localeSetting!,
    direction: form.values.formSetting?.textStyle?.direction ?? Direction.Ltr,
    campaignType: campaignType!,
    application: application!,
    onConfirm: handleConfirm,
    onDirectionChange: handleDirectionChange,
  })

  const localeOptions = useMemo(
    () =>
      availableLocales.map((value) => ({
        label: t('styles.locale.options.' + value),
        value,
      })),
    [t, multilangEnabled]
  )

  const handleLocalChange = (value: Locale) => {
    if (form.values.localeSetting?.locale !== value) {
      form.setFieldValue('localeSetting.locale', value)
      form.setFieldValue('localeSetting.wordings', getDefaultLocaleWords(value))
      if (value !== Locale.Other) {
        form.setFieldValue('formSetting.textStyle.direction', Direction.Ltr)
      }
    }
  }

  return (
    <StyleItem label={t('styles.locale.title')}>
      <Group noWrap>
        <Select data={localeOptions} {...form.getInputProps('localeSetting.locale')} onChange={handleLocalChange} />
        <Button variant="subtle" size="xs" onClick={openModal}>
          {t('styles.locale.edit')}
        </Button>
      </Group>
    </StyleItem>
  )
}

export default WidgetLocale
